import { Link } from "@remix-run/react";

import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "~/components/ui/tooltip";
import { useUserDetails } from "~/hooks/queries/use-user-details";
import { cn } from "~/lib/utils";

// AIDEV-NOTE: Pure display component — no hooks. Exported for gallery and testing.
interface SessionUsageSidebarCardDisplayProps {
    usedUnits: number;
    limitUnits: number;
    /** ISO timestamp when the current billing cycle rolls over. */
    cycleEndAt?: string | null;
    collapsed?: boolean;
    showUpgrade?: boolean;
    upgradeLinkTo?: string;
    className?: string;
}

// AIDEV-NOTE: Cycles are subscription anniversaries, so "resets on the 1st" is wrong for
// almost everyone. Showing the real reset date is the only way a user can reconcile the
// count with their own calendar; without it, a session from the previous calendar month
// sitting inside the current cycle looks like the allowance failed to reset (EVO-2133).
function formatCycleReset(
    cycleEndAt: string | null | undefined,
): string | null {
    if (!cycleEndAt) return null;
    const date = new Date(cycleEndAt);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
    });
}

export function SessionUsageSidebarCardDisplay({
    usedUnits,
    limitUnits,
    cycleEndAt,
    collapsed,
    showUpgrade,
    upgradeLinkTo,
    className,
}: SessionUsageSidebarCardDisplayProps) {
    const atLimit = usedUnits >= limitUnits;
    // AIDEV-NOTE: An unlabelled "2 / 5" is genuinely ambiguous and a practitioner read it
    // as "2 remaining" when it meant "2 used" (EVO-2133). He then reported his allowance
    // as broken. Every rendering of these numbers must say which one is which.
    const remainingUnits = Math.max(limitUnits - usedUnits, 0);
    const resetLabel = formatCycleReset(cycleEndAt);
    const usageLabel = [
        `${usedUnits} of ${limitUnits} sessions used this billing cycle, ${remainingUnits} remaining`,
        resetLabel ? `Resets ${resetLabel}.` : null,
    ]
        .filter(Boolean)
        .join(". ")
        .replace("..", ".");

    if (collapsed) {
        return (
            <div
                className={cn(
                    "flex flex-col items-center gap-0.5",
                    atLimit
                        ? "text-red-700"
                        : "bg-gradient-to-br from-purple-600 via-pink-500 to-emerald-500 bg-clip-text text-transparent",
                    className,
                )}
            >
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div
                            className="flex flex-col items-center gap-0.5"
                            aria-label={usageLabel}
                        >
                            <div className="text-xs font-semibold tabular-nums">
                                {usedUnits}/{limitUnits}
                            </div>
                            <div className="text-[10px] leading-tight">
                                Used
                            </div>
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>{usageLabel}</TooltipContent>
                </Tooltip>
            </div>
        );
    }

    // AIDEV-NOTE: The expanded sidebar is only ~176px wide inside its padding, and
    // "N / N sessions used, resets Mon D" plus the Upgrade link does not fit on one line.
    // Laid out as two nowrap rows so it never wraps mid-value ("0 /" above "5"); do not
    // collapse this back into a single flex row.
    return (
        <div
            className={cn(
                "flex flex-col items-end gap-0.5 text-right leading-tight",
                atLimit
                    ? "text-red-700"
                    : "bg-gradient-to-r from-purple-600 via-pink-500 to-emerald-500 bg-clip-text text-transparent",
                className,
            )}
        >
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className="whitespace-nowrap text-xs"
                        aria-label={usageLabel}
                    >
                        <span className="tabular-nums">
                            {usedUnits} / {limitUnits}
                        </span>{" "}
                        sessions used
                    </div>
                </TooltipTrigger>
                <TooltipContent>{usageLabel}</TooltipContent>
            </Tooltip>

            {(resetLabel || showUpgrade) && (
                <div className="flex items-baseline justify-end gap-2">
                    {resetLabel && (
                        <span className="whitespace-nowrap text-xs">
                            Resets {resetLabel}
                        </span>
                    )}
                    {showUpgrade && (
                        <Link
                            to={upgradeLinkTo ?? "/account/membership"}
                            className="whitespace-nowrap text-xs underline underline-offset-2"
                            prefetch="intent"
                        >
                            Upgrade
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
}

interface SessionUsageSidebarCardProps {
    className?: string;
    collapsed?: boolean;
}

// AIDEV-NOTE: Hooked wrapper — reads billing context and delegates to SessionUsageSidebarCardDisplay.
// Displays session usage progress for practitioners on session-limited plans.
// Renders inline (no card chrome) and adapts to collapsed/expanded sidebar states.
export function SessionUsageSidebarCard({
    className,
    collapsed,
}: SessionUsageSidebarCardProps) {
    const { data } = useUserDetails();
    const billing = data?.billing ?? null;

    if (!billing?.enforced || !billing.entitlement) {
        return null;
    }

    const { metricType, usedUnits, limitUnits, cycleEndAt } =
        billing.entitlement;
    if (metricType !== "session") {
        return null;
    }

    // AIDEV-NOTE: Treat very large limits as effectively unlimited; hide the meter in that case.
    if (limitUnits >= 9999) {
        return null;
    }

    const tierName = billing.tierName;
    const showUpgrade = tierName !== "growth";

    // AIDEV-NOTE: When showUpgrade is true the display renders an inner <Link>,
    // so we must not wrap it in an outer Link (nested anchors are invalid HTML).
    // Collapsed state never shows the inner link, so the outer Link is safe there.
    if (collapsed) {
        return (
            <Link to="/account/membership" prefetch="intent">
                <SessionUsageSidebarCardDisplay
                    usedUnits={usedUnits}
                    limitUnits={limitUnits}
                    cycleEndAt={cycleEndAt}
                    collapsed
                    className={className}
                />
            </Link>
        );
    }

    if (showUpgrade) {
        return (
            <SessionUsageSidebarCardDisplay
                usedUnits={usedUnits}
                limitUnits={limitUnits}
                cycleEndAt={cycleEndAt}
                showUpgrade
                upgradeLinkTo="/account/membership"
                className={className}
            />
        );
    }

    return (
        <Link to="/account/membership" prefetch="intent">
            <SessionUsageSidebarCardDisplay
                usedUnits={usedUnits}
                limitUnits={limitUnits}
                cycleEndAt={cycleEndAt}
                className={className}
            />
        </Link>
    );
}
